import { useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Trash2, Download, Database } from "lucide-react";
import api, { errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function DataTab() {
  const { meta, refreshMeta } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      toast.success(`${msg}: ${Object.entries(data).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      await refreshMeta();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const dl = (p, f) => downloadFile(p, f).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel title={<span className="flex items-center gap-2"><FlaskConical className="h-4 w-4 text-orange-500" />Presentation Demo Mode</span>} testid="demo-panel">
        <div className="text-sm" data-testid="demo-status">{meta.demo.loaded ? <><b className="text-orange-600">Demo data loaded</b> — {meta.demo.lead_count} synthetic leads.</> : "No demo data loaded."}</div>
        <p className="text-xs text-slate-500 mt-2">Loads 20 Dealer, 15 Commercial and 25 Residential synthetic leads with quotations, activities, follow-ups, won/lost outcomes and 4 demo salespeople. All records are flagged DEMO and are not Serato's actual business records. Removal deletes only flagged records — real data is untouched.</p>
        <div className="flex gap-2 mt-4">
          <Button disabled={busy || meta.demo.loaded} onClick={() => run("/demo/load", "Demo data loaded")} className="bg-orange-500 hover:bg-orange-600" data-testid="load-demo-button">Load demo data</Button>
          <Button disabled={busy || !meta.demo.loaded} variant="outline" className="text-red-600" onClick={() => setConfirm(true)} data-testid="remove-demo-button"><Trash2 className="h-4 w-4 mr-1" />Remove demo data</Button>
        </div>
      </Panel>
      <Panel title={<span className="flex items-center gap-2"><Database className="h-4 w-4" />Backup & export</span>} testid="backup-panel">
        <p className="text-sm text-slate-500 mb-4">Download a full JSON backup (passwords excluded) or CSV extracts. Every export is audit-logged.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => dl("/export/backup.json", "salescore_backup.json")} data-testid="backup-json-button"><Download className="h-4 w-4 mr-1" />Full backup (JSON)</Button>
          <Button variant="outline" onClick={() => dl("/export/leads.csv", "salescore_leads.csv")} data-testid="backup-leads-csv-button"><Download className="h-4 w-4 mr-1" />Leads CSV</Button>
          <Button variant="outline" onClick={() => dl("/export/quotations.csv", "salescore_quotations.csv")} data-testid="backup-quotes-csv-button"><Download className="h-4 w-4 mr-1" />Quotations CSV</Button>
        </div>
      </Panel>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="remove-demo-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all demo data?</AlertDialogTitle>
            <AlertDialogDescription>This deletes every record flagged as DEMO (leads, activities, follow-ups, quotations and demo users). Real records are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => run("/demo/remove", "Removed")} data-testid="confirm-remove-demo-button">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
